/**
 * SuggestedRitualsSection Component
 * Displays a carousel of 5 random rituals for discovery.
 * Shown inside TabEmptyState when a tab has no content.
 *
 * Uses optimistic local state for bookmark toggling so that
 * the carousel doesn't refetch (which would randomise the list).
 */

import { useCallback, useMemo, useState } from "react"
import { RitualCard, type RitualCardProps } from "@/components/cards/ritualCard"
import { Carousel } from "@/components/carousel/Carousel"
import { useToggleRitualSave } from "@/features/profile/hooks/useSavedRituals"
import { formatRitualType } from "@/features/rituals/types"
import { formatDuration } from "@/features/rituals/utils/formatDuration"
import { useSuggestedRituals } from "../hooks/useSuggestedRituals"
import type { SuggestedRitual } from "../types/suggested-rituals.types"

function toCardProps(
  rituals: SuggestedRitual[],
  onBookmark: (id: string) => void,
  toggledIds: Set<string>,
): RitualCardProps[] {
  return rituals.map(r => ({
    id: r.id,
    image: r.coverImage ?? undefined,
    userAvatar: r.user?.profilePictureUrl ?? undefined,
    username: r.user?.name ?? "Unknown",
    userHandle: r.user?.username ? `@${r.user.username}` : "",
    title: r.title,
    tags: [formatRitualType(r.ritualType)],
    duration: formatDuration(r.duration),
    isBookmarked: toggledIds.has(r.id) ? !r.isSaved : r.isSaved,
    onBookmarkClick: () => onBookmark(r.id),
    userId: r.user?.id,
    price: r.fee ?? undefined,
    participationFee: r.isDonationBased ? "Donation-based" : r.isPaid ? "Paid" : "Free",
    ritualTs: r.ritualTs,
    city: r.city,
    state: r.state,
  }))
}

export function SuggestedRitualsSection() {
  const { rituals, isLoading } = useSuggestedRituals(5)
  const toggleSave = useToggleRitualSave()
  const [toggledIds, setToggledIds] = useState<Set<string>>(new Set())

  const handleBookmark = useCallback(
    (ritualId: string) => {
      // Optimistically flip the toggled state immediately
      setToggledIds(prev => {
        const next = new Set(prev)
        if (next.has(ritualId)) {
          next.delete(ritualId)
        } else {
          next.add(ritualId)
        }
        return next
      })

      // Fire the API call — rollback on failure
      toggleSave.mutate(ritualId, {
        onError: () => {
          setToggledIds(prev => {
            const next = new Set(prev)
            next.delete(ritualId)
            return next
          })
        },
      })
    },
    [toggleSave],
  )

  const cardData = useMemo(
    () => toCardProps(rituals, handleBookmark, toggledIds),
    [rituals, handleBookmark, toggledIds],
  )

  if (isLoading) {
    return (
      <div className="mt-8">
        <div className="h-5 w-40 bg-white/5 rounded animate-pulse mb-3" />
        <div className="h-4 w-64 bg-white/5 rounded animate-pulse mb-6" />
        <div className="flex gap-3">
          {[1, 2, 3].map(i => (
            <div
              key={i}
              className="h-[200px] md:h-[240px] flex-1 bg-white/5 rounded-tl-[20px] rounded-br-[20px] animate-pulse"
            />
          ))}
        </div>
      </div>
    )
  }

  if (cardData.length === 0) return null

  return (
    <div className="mt-8">
      <h3 className="text-sm md:text-[18px] font-semibold leading-[30px] uppercase text-text-primary">
        Suggested Rituals
      </h3>
      <p className="text-sm text-primary opacity-90 mt-2.5 leading-relaxed">
        Rituals you may want to explore based on your interests.
      </p>

      <div className="mt-5">
        <Carousel
          data={cardData}
          renderItem={ritual => (
            <div className="h-[260px] md:h-[440px] max-w-[640px]">
              <RitualCard {...ritual} />
            </div>
          )}
          loop={false}
          showArrows={false}
          itemsPerScreen={{ sm: 1, md: 2, lg: 2, xl: 2 }}
          opts={{ dragFree: true, align: "start" }}
        />
      </div>
    </div>
  )
}
